import React, { useState, useEffect, useRef } from 'react'
import { Form, Input, Checkbox, Select, Spin, message, Space, Radio, Button } from 'antd'

import { HugeRTEWrapper } from '@veritas/huge-rte-wrapper'
import { useCaseViewContext } from '../../../../context'
import { CaseStatusEnum } from '../../../../constants'
import {
    PatientMessageHeader,
    SectionTitle,
    AdjustmentFormItem,
    ConsultationForm,
} from './CompleteConsultationForm.styles'

const RejectionReason = () => (
    <div>
        <Form.Item
            label="Rejection Reason (to be provided to reviewing physician)"
            name="rejectionReason"
            required
        >
            <Input.TextArea
                id="complete-consult-rejection-reason-textarea"
                rows={4}
                placeholder="Provide a reason for rejection..."
            />
        </Form.Item>
    </div>
)

const PatientMessage = ({ 
    form, 
    patientMessage, 
    onPatientMessageChange, 
    onClearPatientMessage 
}) => {
    const { caseDetails, provider, apiClasses } = useCaseViewContext()
    const [loading, setLoading] = useState(true)
    const [templateType, selectTemplateType] = useState('my')
    const [availableTemplates, setAvailableTemplates] = useState([])
    const editorRef = useRef(null)

    const getTemplateMessages = () => {
        if (!apiClasses?.PhysicianAPI) {
            setLoading(false)
            return
        }

        const filters = {
            categoryIds: [caseDetails?.categoryId],
            status: CaseStatusEnum.CONSULT_COMPLETE,
        }

        if (templateType === 'my') {
            filters.owner = provider.userId
        }

        apiClasses.PhysicianAPI.getTemplates('case', { filters })
            .then(({ data: { templates } }) => setAvailableTemplates(templates.sort(a => a.favoritedBy ? -1 : 1)))
            .catch(() => message.error('Error loading patient message templates'))
            .finally(() => setLoading(false))
    }

    const handleTemplateSelect = message => {
        const currentMessage = form.getFieldValue('patientExplanation') || ``
        const appendedMessage = currentMessage + `\n\n${message}`
        form.setFieldsValue({ patientExplanation: appendedMessage })
        onPatientMessageChange(appendedMessage)
        
        setTimeout(() => {
            const editor = editorRef.current?.editor
            if (editor) {
                    const contentHeight = editor.getBody().scrollHeight;
                    editor.getWin().scrollTo(0, contentHeight);
                }
        }, 0)
    }

    useEffect(() => {
        getTemplateMessages()
    }, [templateType, apiClasses, caseDetails, provider])

    return (
        <Spin spinning={loading}>
            <PatientMessageHeader>
                <SectionTitle strong>
                    Patient Message
                </SectionTitle>
                <Button id="complete-consult-clear-patient-message-btn" onClick={onClearPatientMessage}>
                    Clear
                </Button>
            </PatientMessageHeader>
            <Form.Item label='Select a patient message template'>
                <Form.Item>
                    <Select id="complete-consult-template-type-select" value={templateType} onChange={selectTemplateType}>
                        <Select.Option value='custom'>Freeform Message</Select.Option>
                        <Select.Option value='shared'>Shared Templates</Select.Option>
                        <Select.Option value='my'>My Templates</Select.Option>
                        <Select.Option value='admin'>Admin Templates</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item name="templateName">
                    <Select
                        id="complete-consult-template-name-select"
                        placeholder="Select a template"
                        disabled={templateType === 'custom'}
                        onChange={handleTemplateSelect}
                        options={availableTemplates.map(template => ({ key: template._id, label: template.title, value: template.message }))}
                    />
                </Form.Item>
            </Form.Item>
            <Form.Item required label="Patient explanation" name="patientExplanation">
                <HugeRTEWrapper 
                    ref={editorRef}
                    onChange={onPatientMessageChange}
                    value={patientMessage}
                />
            </Form.Item>
        </Spin>
    )
}

const RxMismatchCheck = ({ isRxMismatch }) => {
    if (!isRxMismatch) return null
    return (
        <div>
            <SectionTitle strong>
                Prescription Adjustment Reason
            </SectionTitle>
            <span>
                We noticed there might be some variation between what was requested by the patient
                and what was prescribed. Please select a reason below:
            </span>
            <AdjustmentFormItem
                label=""
                defaultValue="patient"
                name="adjustmentReason"
            >
                <Radio.Group id="complete-consult-adjustment-reason-group" defaultValue={null}>
                    <Space direction="vertical">
                        <Radio id="complete-consult-adjustment-no-adjustment-radio" value={null}>No Adjustment</Radio>
                        <Radio id="complete-consult-adjustment-by-provider-radio" value="Rx Adjustment by Dr">Rx Adjustment by Provider</Radio>
                        <Radio id="complete-consult-adjustment-by-patient-radio" value="Rx Adjustment by Patient" checked>Rx Adjustment by Patient</Radio>
                    </Space>
                </Radio.Group>
            </AdjustmentFormItem>
        </div>
    )
}

export const CompleteConsultationForm = ({ 
    form, 
    prescriptions = [], 
    patientMessage, 
    onPatientMessageChange, 
    onClearPatientMessage,
    includePatientMessage,
    onIncludePatientMessageChange,
}) => {
    const { provider } = useCaseViewContext()
    
    const providerNeedsReview = provider?.tags?.includes('needs review')
    const isRxMismatch = prescriptions && prescriptions.some(med => med.mismatch)

    return (
        <ConsultationForm
            layout="vertical"
            form={form}
        >
            <RxMismatchCheck isRxMismatch={isRxMismatch}/>
            <Checkbox
                id="complete-consult-include-patient-message-checkbox"
                checked={includePatientMessage}
                onChange={e => onIncludePatientMessageChange(e.target.checked)}
            >
                Include patient message
            </Checkbox>
            { includePatientMessage && (
                <PatientMessage 
                    form={form} 
                    patientMessage={patientMessage} 
                    onPatientMessageChange={onPatientMessageChange} 
                    onClearPatientMessage={onClearPatientMessage}
                />
            )}
            { !prescriptions.length && providerNeedsReview && <RejectionReason />}
        </ConsultationForm>
    )
}
